import * as React from "react";
import * as XLSX from "xlsx";
import { PageHeader } from "@/components/common/PageHeader";
import { NotConfiguredBanner } from "@/components/common/NotConfiguredBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, RefreshCw, Search, TrendingUp, AlertCircle, FileSpreadsheet } from "lucide-react";
import { useAuth } from "@/supabase/AuthContext";
import { createBrowserSupabase } from "@/supabase/client";
import type { CanonicalProductSalesRow } from "@/features/nebim/types";

type ImportBatch = {
  id: string;
  original_filename: string | null;
  status: string;
  created_at: string;
  source_period_start?: string | null;
  source_period_end?: string | null;
  total_rows: number | null;
  valid_rows: number | null;
  error_rows: number | null;
  error_summary: unknown;
};

type InvoiceVatLine = { source_product_code: string | null; vat_rate: number | null; product_id?: string | null };
type ProductVariantCode = { product_id: string; product_code: string };
type SalesRow = {
  id: string;
  source_row_number: number;
  row_status: string;
  normalized_payload: CanonicalProductSalesRow | null;
};

const PAGE_SIZE = 1000;

function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(value);
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

const numericFields: Array<keyof CanonicalProductSalesRow> = [
  "gross_qty", "return_qty", "net_qty", "gross_sales_vh", "gross_sales_vd",
  "return_vh", "return_vd", "net_sales_vh", "net_sales_vd",
  "gross_discount", "return_discount", "total_discount",
];

const DailySales: React.FC = () => {
  const { companyId } = useAuth();
  const factory = React.useMemo(() => createBrowserSupabase(), []);
  const [batch, setBatch] = React.useState<ImportBatch | null>(null);
  const [rows, setRows] = React.useState<SalesRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [selectedDate, setSelectedDate] = React.useState("");
  const [appliedDate, setAppliedDate] = React.useState("");
  const [vatRatesBySku, setVatRatesBySku] = React.useState<Record<string, number | null>>({});
  const [vatRateWarning, setVatRateWarning] = React.useState<string | null>(null);
  const salesRequestId = React.useRef(0);

  const loadSales = React.useCallback(async () => {
    const requestId = ++salesRequestId.current;
    if (!factory.ok || !companyId) {
      // Never leave another company's report visible after a session/company change.
      setBatch(null);
      setRows([]);
      setVatRatesBySku({});
      setVatRateWarning(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const BATCH_PAGE_SIZE = 500;
      const BATCH_SCAN_CAP = 10000;
      let latest: ImportBatch | undefined;
      let scannedAllAvailable = false;
      for (let start = 0; start < BATCH_SCAN_CAP; start += BATCH_PAGE_SIZE) {
        const { data: batches, error: batchError } = await factory.client
          .from("import_batches")
          .select("id, original_filename, status, created_at, source_period_start, source_period_end, total_rows, valid_rows, error_rows, error_summary")
          .eq("company_id", companyId)
          .eq("import_type", "nebim_sales_daily")
          .eq("status", "committed")
          .order("created_at", { ascending: false })
          .range(start, start + BATCH_PAGE_SIZE - 1);
        if (requestId !== salesRequestId.current) return;
        if (batchError) throw batchError;
        const page = (batches ?? []) as ImportBatch[];
        latest = appliedDate ? page.find((item) => item.source_period_start === appliedDate) : page[0];
        if (latest) break;
        if (page.length < BATCH_PAGE_SIZE) {
          scannedAllAvailable = true;
          break;
        }
      }
      if (!latest && !scannedAllAvailable) {
        throw new Error("Son 10.000 satış aktarımı tarandı ancak gerçek IFM satış dosyası bulunamadı. Eksik geçmiş veriyi yokmuş gibi göstermemek için rapor durduruldu.");
      }
      if (!latest) {
        setBatch(null);
        setRows([]);
        setVatRatesBySku({});
        setVatRateWarning(null);
        return;
      }

      const collected: SalesRow[] = [];
      for (let start = 0; start < 20000; start += PAGE_SIZE) {
        const { data, error: rowsError } = await factory.client
          .from("import_rows")
          .select("id, source_row_number, row_status, normalized_payload")
          .eq("company_id", companyId)
          .eq("batch_id", latest.id)
          .order("source_row_number", { ascending: true })
          .range(start, start + PAGE_SIZE - 1);
        if (requestId !== salesRequestId.current) return;
        if (rowsError) throw rowsError;
        const page = (data ?? []) as SalesRow[];
        collected.push(...page);
        if (page.length < PAGE_SIZE) break;
        if (start + PAGE_SIZE >= 20000) {
          throw new Error("Satış raporu 20.000 satır sınırına ulaştı. Eksik toplam göstermemek için rapor durduruldu; veriyi bölerek aktarın.");
        }
      }
      const taxRates: Record<string, number | null> = {};
      let taxWarning: string | null = null;
      try {
        const { data: invoiceRows, error: invoiceError } = await factory.client.from("supplier_invoices")
          .select("id").eq("company_id", companyId).eq("status", "finalized").eq("test_only", false);
        if (invoiceError) throw invoiceError;
        const invoiceIds = (invoiceRows ?? []).map((item: { id: string }) => item.id);
        const rateSets = new Map<string, Set<number>>();
        const productRateSets = new Map<string, Set<number>>();
        if (invoiceIds.length) {
          for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
            const { data, error: lineError } = await factory.client.from("supplier_invoice_lines")
              .select("source_product_code, vat_rate, product_id").eq("company_id", companyId)
              .in("invoice_id", invoiceIds).range(offset, offset + PAGE_SIZE - 1);
            if (lineError) throw lineError;
            const page = (data ?? []) as InvoiceVatLine[];
            for (const line of page) {
              const sku = (line.source_product_code ?? "").trim().toLocaleLowerCase("tr-TR");
              const rate = Number(line.vat_rate);
              if ((!sku && !line.product_id) || line.vat_rate == null || !Number.isFinite(rate) || rate < 0 || rate > 100) continue;
              if (sku) {
                const set = rateSets.get(sku) ?? new Set<number>();
                set.add(rate);
                rateSets.set(sku, set);
              }
              if (line.product_id) {
                const productSet = productRateSets.get(line.product_id) ?? new Set<number>();
                productSet.add(rate);
                productRateSets.set(line.product_id, productSet);
              }
            }
            if (page.length < PAGE_SIZE) break;
            if (offset + PAGE_SIZE >= 50000) throw new Error("Fatura KDV oranları 50.000 satır sınırına ulaştı.");
          }
        }
        if (productRateSets.size) {
          for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
            const { data, error: variantError } = await factory.client.from("product_variants")
              .select("product_id, product_code").eq("company_id", companyId)
              .range(offset, offset + PAGE_SIZE - 1);
            if (variantError) throw variantError;
            const page = (data ?? []) as ProductVariantCode[];
            for (const variant of page) {
              const rates = productRateSets.get(variant.product_id);
              const sku = (variant.product_code ?? "").trim().toLocaleLowerCase("tr-TR");
              if (!sku || !rates) continue;
              const set = rateSets.get(sku) ?? new Set<number>();
              for (const rate of rates) set.add(rate);
              rateSets.set(sku, set);
            }
            if (page.length < PAGE_SIZE) break;
            if (offset + PAGE_SIZE >= 50000) throw new Error("Ürün kataloğu KDV eşlemesi 50.000 satır sınırına ulaştı.");
          }
        }
        // Bridge invoice VAT rates to Nebim sales codes through approved variant mappings.
        // This is conservative: a source SKU is assigned a rate only when every matching
        // mapped variant resolves to one identical VAT rate.
        const variantParentById = new Map<string, string>();
        for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
          const { data, error: variantError } = await factory.client.from("product_variants")
            .select("id, product_id").eq("company_id", companyId)
            .range(offset, offset + PAGE_SIZE - 1);
          if (variantError) throw variantError;
          const page = (data ?? []) as Array<{ id: string; product_id: string }>;
          for (const variant of page) variantParentById.set(variant.id, variant.product_id);
          if (page.length < PAGE_SIZE) break;
          if (offset + PAGE_SIZE >= 50000) throw new Error("Ürün varyantları KDV eşlemesi 50.000 satır sınırına ulaştı.");
        }
        const mappedSkuRates = new Map<string, Set<number>>();
        for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
          const { data, error: mappingError } = await factory.client.from("product_mappings")
            .select("source_product_code, product_variant_id")
            .eq("company_id", companyId).eq("source_system", "nebim").eq("status", "approved")
            .range(offset, offset + PAGE_SIZE - 1);
          if (mappingError) throw mappingError;
          const page = (data ?? []) as Array<{ source_product_code: string | null; product_variant_id: string | null }>;
          for (const mapping of page) {
            const sku = (mapping.source_product_code ?? "").trim().toLocaleLowerCase("tr-TR");
            const parentId = mapping.product_variant_id ? variantParentById.get(mapping.product_variant_id) : undefined;
            const rates = parentId ? productRateSets.get(parentId) : undefined;
            if (!sku || !rates) continue;
            const set = mappedSkuRates.get(sku) ?? new Set<number>();
            for (const rate of rates) set.add(rate);
            mappedSkuRates.set(sku, set);
          }
          if (page.length < PAGE_SIZE) break;
          if (offset + PAGE_SIZE >= 50000) throw new Error("Ürün eşleştirmeleri KDV çözümlemesi 50.000 satır sınırına ulaştı.");
        }
        for (const [sku, rates] of mappedSkuRates) {
          const existing = rateSets.get(sku) ?? new Set<number>();
          for (const rate of rates) existing.add(rate);
          rateSets.set(sku, existing);
        }
        for (const [sku, rates] of rateSets) taxRates[sku] = rates.size === 1 ? [...rates][0] : null;
      } catch (taxError) {
        const detail = taxError as { message?: unknown };
        taxWarning = "Faturalardan KDV oranları okunamadı; eksik VD değerleri hesaplanmadı." +
          (typeof detail?.message === "string" ? " " + detail.message : "");
      }

      setBatch(latest);
      setRows(collected);
      setVatRatesBySku(taxRates);
      setVatRateWarning(taxWarning);
      if (!appliedDate && latest.source_period_start) {
        setSelectedDate(latest.source_period_start);
        setAppliedDate(latest.source_period_start);
      }
    } catch (e) {
      if (requestId !== salesRequestId.current) return;
      const err = e as { message?: unknown };
      // Clear previous results on refresh failure; stale totals must not look current.
      setBatch(null);
      setRows([]);
      setVatRatesBySku({});
      setVatRateWarning(null);
      setError(typeof err?.message === "string" ? err.message : "Satış raporu yüklenemedi.");
    } finally {
      if (requestId === salesRequestId.current) setLoading(false);
    }
  }, [factory, companyId, appliedDate]);

  React.useEffect(() => {
    void loadSales();
    return () => {
      salesRequestId.current += 1;
    };
  }, [loadSales]);

  const reportRows = React.useMemo(() => rows
    .filter((row) => row.normalized_payload)
    .map((row) => ({ ...row, sales: row.normalized_payload as CanonicalProductSalesRow })), [rows]);

  const filteredRows = React.useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");
    return reportRows.filter(({ sales, row_status }) => {
      if (statusFilter !== "all" && row_status !== statusFilter) return false;
      if (!query) return true;
      return [sales.sku, sales.barcode, sales.product_name, sales.color_code, sales.color_name, sales.size]
        .some((value) => String(value ?? "").toLocaleLowerCase("tr-TR").includes(query));
    });
  }, [reportRows, search, statusFilter]);

  const inclusiveSalesById = React.useMemo(() => {
    const result: Record<string, number | null> = {};
    for (const row of reportRows) {
      const vh = Number(row.sales.net_sales_vh ?? 0);
      const vd = Number(row.sales.net_sales_vd ?? 0);
      const sku = (row.sales.sku ?? "").trim().toLocaleLowerCase("tr-TR");
      const rate = vatRatesBySku[sku];
      if (vd !== 0 || vh === 0) result[row.id] = vd;
      else if (rate != null) result[row.id] = Math.round((vh * (1 + rate / 100) + Number.EPSILON) * 100) / 100;
      else result[row.id] = null;
    }
    return result;
  }, [reportRows, vatRatesBySku]);

  const missingVatRateRows = React.useMemo(() => reportRows.filter((row) =>
    row.row_status !== "error" && Number(row.sales.net_sales_vh ?? 0) !== 0 && Number(row.sales.net_sales_vd ?? 0) === 0 && inclusiveSalesById[row.id] == null
  ).length, [reportRows, inclusiveSalesById]);

  const totals = React.useMemo(() => {
    const result: Record<string, number> = {};
    for (const field of numericFields) result[field] = 0;
    for (const row of reportRows) {
      if (row.row_status === "error") continue;
      for (const field of numericFields) {
        if (field === "net_sales_vd") {
          const value = inclusiveSalesById[row.id];
          if (value != null) result[field] += value;
          continue;
        }
        const value = row.sales[field];
        if (typeof value === "number" && Number.isFinite(value)) result[field] += value;
      }
    }
    return result;
  }, [reportRows, inclusiveSalesById]);

  const exportRows = () => {
    const exportData = filteredRows.map(({ id, sales, row_status, source_row_number }) => ({
      "Excel Satır No": source_row_number,
      "Durum": row_status,
      "Ürün Kodu": sales.sku ?? "",
      "Ürün Adı": sales.product_name ?? "",
      "Barkod": sales.barcode ?? "",
      "Renk Kodu": sales.color_code ?? "",
      "Renk": sales.color_name ?? "",
      "Beden": sales.size ?? "",
      "Brüt Miktar": sales.gross_qty ?? "",
      "İade Miktarı": sales.return_qty ?? "",
      "Net Miktar": sales.net_qty ?? "",
      "Brüt Satış (VH)": sales.gross_sales_vh ?? "",
      "Brüt Satış (VD)": sales.gross_sales_vd ?? "",
      "İade (VH)": sales.return_vh ?? "",
      "İade (VD)": sales.return_vd ?? "",
      "Satış (VH)": sales.net_sales_vh ?? "",
      "Satış (KDV dahil)": inclusiveSalesById[id] ?? "",
      "Toplam İskonto": sales.total_discount ?? "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Satış Raporu");
    XLSX.writeFile(workbook, "ifm-satis-raporu.xlsx");
  };

  if (!factory.ok) {
    return (
      <div data-testid="page-daily-sales" className="space-y-8">
        <PageHeader eyebrow="Analiz" title="Satış Raporu" description="İçe aktarılan Nebim ürün bazlı satış raporunu inceleyin." />
        <NotConfiguredBanner />
      </div>
    );
  }

  return (
    <div data-testid="page-daily-sales" className="space-y-8">
      <PageHeader
        eyebrow="Analiz"
        title="Satış Raporu"
        description="Tarihini seçtiğiniz Nebim ürün bazlı satış raporunu inceleyin, doğrulama durumuna göre süzün ve Excel'e aktarın."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => void loadSales()} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile
            </Button>
            <Button size="sm" onClick={exportRows} disabled={loading || filteredRows.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Excel'e aktar
            </Button>
          </>
        }
      />

      <section className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
        <strong>Rapor kapsamı:</strong> Bu ekran ürün bazında toplu Nebim raporunu gösterir; satırlarda fiş tarihi ve fiş numarası bulunmadığından günlük ciro grafiği veya fiş bazlı analiz üretmez. Özet toplamlar yalnızca hata durumunda olmayan satırlardan hesaplanır.
      </section>

      {error ? <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"><AlertCircle className="mr-2 inline h-4 w-4" />Satış raporu yüklenemedi: {error}</div> : null}

      <section aria-label="Rapor tarihi seçimi" className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4">
        <div className="min-w-56">
          <label htmlFor="sales-report-date" className="mb-2 block text-sm font-medium">Satış raporu tarihi</label>
          <Input id="sales-report-date" type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} aria-label="Satış raporu tarihini seç" />
        </div>
        <Button onClick={() => setAppliedDate(selectedDate)} disabled={loading || !selectedDate}>
          <Search className="mr-2 h-4 w-4" /> Bu tarihin verilerini getir
        </Button>
        {appliedDate ? <span className="text-sm text-muted-foreground">Gösterilen tarih: {new Date(appliedDate + "T12:00:00").toLocaleDateString("tr-TR")}</span> : null}
      </section>

      {vatRateWarning ? <p role="status" className="text-sm text-amber-700">{vatRateWarning}</p> : null}
      {missingVatRateRows > 0 ? <p role="status" className="text-sm text-amber-700">{missingVatRateRows} ürün satırında kesinleşmiş faturadan tekil KDV oranı bulunamadı. Bu satırlar KDV dahil toplamına dahil edilmedi; toplam eksik olabilir.</p> : null}

      <section aria-label="Satış özeti" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Net satış miktarı", value: formatNumber(totals.net_qty), icon: TrendingUp },
          { label: "Satış (VH)", value: formatMoney(totals.net_sales_vh), icon: FileSpreadsheet },
          { label: "Satış (KDV dahil)", value: formatMoney(totals.net_sales_vd), icon: FileSpreadsheet },
          { label: "Toplam iskonto", value: formatMoney(totals.total_discount), icon: TrendingUp },
        ].map((card) => (
          <div key={card.label} className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center justify-between text-sm text-muted-foreground">{card.label}<card.icon className="h-4 w-4" /></div>
            <div className="num mt-3 text-2xl font-medium text-foreground">{loading && !batch ? "…" : card.value}</div>
          </div>
        ))}
      </section>

      {batch ? (
        <section className="rounded-lg border border-border bg-card p-4 text-sm">
          <div className="font-medium text-foreground">Kaynak dosya: {batch.original_filename ?? "Dosya adı yok"}</div>
          <div className="mt-1 text-muted-foreground">Aktarım: {new Date(batch.created_at).toLocaleString("tr-TR")} · Kaydedilen satır: {batch.total_rows ?? "—"} · Geçerli: {batch.valid_rows ?? "—"} · Hatalı: {batch.error_rows ?? "—"}</div>
        </section>
      ) : null}

      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_220px]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Ürün kodu, barkod, ürün adı, renk veya beden ara" className="pl-9" aria-label="Satış raporunda ara" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger aria-label="Doğrulama durumuna göre filtrele"><SelectValue placeholder="Tüm durumlar" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm durumlar</SelectItem>
              <SelectItem value="valid">Geçerli</SelectItem>
              <SelectItem value="warning">Uyarılı</SelectItem>
              <SelectItem value="error">Hatalı</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? <div className="p-8 text-center text-sm text-muted-foreground">Satış raporu yükleniyor…</div> : null}
        {!loading && !error && !batch ? (
          <div className="p-8 text-center">
            <TrendingUp className="mx-auto h-8 w-8 text-muted-foreground" />
            <h3 className="mt-3 font-medium text-foreground">Henüz gerçek IFM satış raporu yok</h3>
            <p className="mt-1 text-sm text-muted-foreground">{appliedDate ? `${new Date(appliedDate + "T12:00:00").toLocaleDateString("tr-TR")} tarihinde kaydedilmiş satış raporu bulunamadı. Nebim içe aktarmada raporun kaynak tarihini kontrol edin.` : "Nebim içe aktarma bölümünden ürün bazlı satış raporu kaydedildiğinde burada görüntülenecek."}</p>
          </div>
        ) : null}
        {!loading && batch && filteredRows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Filtrelerle eşleşen satır bulunamadı.</div> : null}

        {!loading && filteredRows.length > 0 ? (
          <>
            <div className="text-xs text-muted-foreground">{filteredRows.length.toLocaleString("tr-TR")} satır gösteriliyor</div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-secondary/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-3">Durum</th><th className="px-3 py-3">Ürün kodu</th><th className="px-3 py-3">Ürün</th><th className="px-3 py-3">Renk / beden</th><th className="px-3 py-3 text-right">Brüt miktar</th><th className="px-3 py-3 text-right">İade</th><th className="px-3 py-3 text-right">Net miktar</th><th className="px-3 py-3 text-right">Satış (VH)</th><th className="px-3 py-3 text-right">Satış (KDV dahil)</th><th className="px-3 py-3 text-right">İskonto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredRows.map(({ id, sales, row_status }) => (
                    <tr key={id}>
                      <td className="whitespace-nowrap px-3 py-3"><span className={`rounded border px-2 py-1 text-xs ${row_status === "error" ? "border-destructive/40 text-destructive" : row_status === "warning" ? "border-amber-500/40 text-amber-800" : "border-emerald-500/30 text-emerald-700"}`}>{row_status === "error" ? "Hatalı" : row_status === "warning" ? "Uyarılı" : "Geçerli"}</span></td>
                      <td className="px-3 py-3 font-mono text-xs">{sales.sku ?? "—"}</td>
                      <td className="min-w-48 px-3 py-3">{sales.product_name ?? "—"}<div className="text-xs text-muted-foreground">{sales.barcode ?? ""}</div></td>
                      <td className="px-3 py-3">{[sales.color_name ?? sales.color_code, sales.size].filter(Boolean).join(" / ") || "—"}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{formatNumber(sales.gross_qty)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{formatNumber(sales.return_qty)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{formatNumber(sales.net_qty)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{formatMoney(sales.net_sales_vh ?? 0)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{inclusiveSalesById[id] == null ? "—" : formatMoney(inclusiveSalesById[id] as number)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{formatMoney(sales.total_discount ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

export default DailySales;
